import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Bell, Calendar, X } from "lucide-react";
import { apiService } from "../utils/api";
import { extractYouTubeVideoId, YouTubePlayer } from "../components/shared/VideoPlayer";
import { ChannelCard } from "../components/shared/CategorySidebar";
import { Footer } from "../components/shared/Footer";

const PremiereBanner = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const premiereDate = new Date('2026-09-01T20:00:00');
    
    const updateCountdown = () => {
      const now = new Date();
      const difference = premiereDate - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      }
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="relative overflow-hidden" data-testid="premiere-banner">
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-r from-red-900 via-black to-red-900" />
      <div className="absolute inset-0 opacity-20" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}} />
      
      <div className="relative px-4 md:px-12 py-8 md:py-12">
        {/* Premiere Header */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-red-600 px-4 py-2 rounded-full text-sm font-bold mb-4 animate-bounce">
            <span className="w-2 h-2 bg-white rounded-full animate-ping" />
            WORLD PREMIERE
          </div>
          <h2 className="text-3xl md:text-5xl font-black text-white mb-2">
            SEPTEMBER 1, 2026
          </h2>
          <p className="text-gray-300 text-lg">Two Exclusive PPV Events You Can't Miss</p>
        </div>

        {/* Countdown Timer */}
        <div className="flex justify-center gap-4 md:gap-8 mb-8">
          <CountdownUnit value={timeLeft.days} label="DAYS" />
          <CountdownUnit value={timeLeft.hours} label="HOURS" />
          <CountdownUnit value={timeLeft.minutes} label="MINS" />
          <CountdownUnit value={timeLeft.seconds} label="SECS" />
        </div>

        {/* Show Cards */}
        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Cheater Beaters */}
          <div className="group relative rounded-2xl overflow-hidden border-2 border-red-600 hover:border-white transition-all transform hover:scale-[1.02]">
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent z-10" />
            <img 
              src="https://customer-assets.emergentagent.com/job_e61a3eb0-b8aa-41f5-bf7d-d0aae0a0bea2/artifacts/js0a4649_CheaterBeaters%281%29.png"
              alt="Cheater Beaters"
              className="w-full h-64 md:h-80 object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-red-600 text-white text-xs px-3 py-1 rounded-full font-bold">PREMIERE</span>
                <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">8:00 PM ET</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-2">CHEATER BEATERS</h3>
              <p className="text-gray-300 text-sm mb-4">Exposing relationship deception and infidelity. The truth will be revealed.</p>
              <button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors">
                <Bell size={18} />
                Set Reminder
              </button>
            </div>
          </div>

          {/* Incognito */}
          <div className="group relative rounded-2xl overflow-hidden border-2 border-red-600 hover:border-white transition-all transform hover:scale-[1.02]">
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent z-10" />
            <img 
              src="https://customer-assets.emergentagent.com/job_e61a3eb0-b8aa-41f5-bf7d-d0aae0a0bea2/artifacts/mllzeqf1_Incognito.jpeg"
              alt="Incognito"
              className="w-full h-64 md:h-80 object-cover group-hover:scale-110 transition-transform duration-500"
            />
            <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
              <div className="flex items-center gap-2 mb-2">
                <span className="bg-red-600 text-white text-xs px-3 py-1 rounded-full font-bold">PREMIERE</span>
                <span className="bg-white/20 text-white text-xs px-3 py-1 rounded-full">10:00 PM ET</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-2">INCOGNITO</h3>
              <p className="text-gray-300 text-sm mb-4">Undercover investigations and secret revelations. Nothing stays hidden.</p>
              <button className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-colors">
                <Bell size={18} />
                Set Reminder
              </button>
            </div>
          </div>
        </div>

        {/* Subscribe CTA */}
        <div className="text-center mt-8">
          <p className="text-gray-400 mb-4">Subscribe now to watch both premieres live</p>
          <button 
            onClick={() => alert('Subscription coming soon! PPV shows will be available for purchase.')}
            className="bg-white text-black font-bold px-8 py-4 rounded-lg hover:bg-gray-200 transition-colors text-lg"
            data-testid="subscribe-btn"
          >
            Subscribe to CBXtra • $9.99/mo
          </button>
        </div>
      </div>
    </section>
  );
};

const CountdownUnit = ({ value, label }) => (
  <div className="text-center">
    <div className="bg-black/50 border border-red-600 rounded-lg px-4 py-3 md:px-6 md:py-4 min-w-[70px] md:min-w-[90px]">
      <span className="text-2xl md:text-4xl font-black text-white">{String(value).padStart(2, '0')}</span>
    </div>
    <span className="text-xs text-gray-400 mt-2 block">{label}</span>
  </div>
);

const PPVCard = ({ show, onWatch }) => (
  <div className="group relative cursor-pointer transform hover:scale-105 transition-all" data-testid={`ppv-card-${show.id}`}>
    <div className="relative aspect-[2/3] rounded-xl overflow-hidden border-2 border-transparent hover:border-red-600">
      <img src={show.thumbnail} alt={show.title} className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      
      <div className="absolute top-3 right-3 space-y-2">
        <div className="bg-red-600 text-white text-xs px-3 py-1.5 rounded-full font-bold">${show.price_monthly}/mo</div>
        <div className="bg-white text-black text-xs px-3 py-1.5 rounded-full font-bold">${show.price_yearly}/yr</div>
      </div>
      
      {show.category === 'podcast' && (
        <div className="absolute top-3 left-3 bg-black/80 text-red-500 text-xs px-3 py-1.5 rounded-full font-bold border border-red-500">PODCAST</div>
      )}
      
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h4 className="text-white font-bold text-lg mb-1">{show.title}</h4>
        <p className="text-gray-300 text-sm line-clamp-2">{show.description}</p>
        <button 
          onClick={() => onWatch && onWatch(show)}
          className="mt-3 w-full bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 rounded-lg"
          data-testid={`watch-btn-${show.id}`}
        >
          Watch Now
        </button>
      </div>
    </div>
  </div>
);

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ppvShows, setPpvShows] = useState([]);
  const [featuredChannel, setFeaturedChannel] = useState(null);
  const [selectedPPV, setSelectedPPV] = useState(null);
  const navigate = useNavigate();

  const handleWatchPPV = (show) => {
    if (show.video_url || show.youtube_url) {
      setSelectedPPV(show);
    } else {
      alert(`"${show.title}" - Coming Soon! Subscribe to CBXtra to be notified when this show is available.`);
    }
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsRes, categoriesRes, ppvRes] = await Promise.all([
          apiService.getChannels(),
          apiService.getCategories(),
          apiService.getPPVShows()
        ]);
        setChannels(channelsRes.data);
        setCategories(categoriesRes.data);
        setPpvShows(ppvRes.data);
        if (channelsRes.data.length > 0) {
          setFeaturedChannel(channelsRes.data[0]);
        }
        setLoading(false);
      } catch (error) {
        console.error('Error:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="home-page">
      {/* PREMIERE BANNER - September 1, 2026 */}
      <div className="pt-16">
        <PremiereBanner />
      </div>

      {/* Hero Section */}
      {featuredChannel && (
        <div className="relative h-[60vh] w-full">
          <div className="absolute inset-0">
            <img 
              src={featuredChannel.current_program?.thumbnail || featuredChannel.logo}
              alt="" 
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          </div>
          
          <div className="relative h-full flex items-center px-4 md:px-12">
            <div className="max-w-2xl space-y-4">
              <div className="flex items-center gap-2 text-red-500 text-sm font-medium">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                NOW PLAYING ON {featuredChannel.name.toUpperCase()}
              </div>
              <h1 className="text-4xl md:text-6xl font-bold">
                {featuredChannel.current_program?.title || featuredChannel.name}
              </h1>
              <p className="text-gray-300 text-lg">
                {featuredChannel.current_program?.description || featuredChannel.description}
              </p>
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={() => navigate('/live-tv')}
                  className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded font-medium"
                >
                  <Play size={20} fill="white" />
                  Watch Live
                </button>
                <button 
                  onClick={() => navigate('/guide')}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded font-medium"
                >
                  <Calendar size={20} />
                  TV Guide
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PPV Section */}
      <section className="px-4 md:px-12 py-8 -mt-20 relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <h2 className="text-2xl font-bold">CBXtra PPV</h2>
          <span className="bg-red-600 px-3 py-1 rounded text-xs font-bold">EXCLUSIVE</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {ppvShows.map((show) => (
            <PPVCard key={show.id} show={show} onWatch={handleWatchPPV} />
          ))}
        </div>
      </section>

      {/* Channel Categories */}
      {categories.slice(0, 6).map((category) => {
        const categoryChannels = channels.filter(ch => ch.category_id === category.id);
        if (categoryChannels.length === 0) return null;
        return (
          <section key={category.id} className="px-4 md:px-12 py-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <span>{category.icon}</span>
                {category.name}
              </h3>
              <button 
                onClick={() => navigate('/live-tv')}
                className="text-red-500 text-sm hover:underline"
              >
                See All
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {categoryChannels.slice(0, 5).map((channel) => (
                <ChannelCard 
                  key={channel.id} 
                  channel={channel}
                  onSelect={() => navigate('/live-tv')}
                />
              ))}
            </div>
          </section>
        );
      })}

      <Footer />

      {/* PPV Video Modal */}
      {selectedPPV && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" data-testid="ppv-modal">
          <div className="bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-800">
              <h3 className="text-xl font-bold text-white">{selectedPPV.title}</h3>
              <button 
                onClick={() => setSelectedPPV(null)}
                className="text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            <div className="aspect-video bg-black">
              {extractYouTubeVideoId(selectedPPV.youtube_url) ? (
                <YouTubePlayer 
                  videoId={extractYouTubeVideoId(selectedPPV.youtube_url)}
                  autoplay={true}
                  title={selectedPPV.title}
                />
              ) : selectedPPV.video_url ? (
                <video 
                  src={selectedPPV.video_url} 
                  controls 
                  autoPlay 
                  className="w-full h-full"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <Play size={64} className="text-red-600 mx-auto mb-4" />
                    <p className="text-gray-400">Video coming soon</p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-4">
              <p className="text-gray-300">{selectedPPV.description}</p>
              <div className="flex items-center gap-4 mt-4">
                <span className="bg-red-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                  ${selectedPPV.price_monthly}/mo
                </span>
                <span className="bg-gray-700 text-white px-3 py-1 rounded-full text-sm">
                  {selectedPPV.category}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
